import React, { useEffect, useState } from 'react';
import { ShieldAlert, Trash2, Plus, Smartphone, AlertTriangle } from 'lucide-react';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';

interface Contact { id: string; name?: string; phone: string; }

interface Props {
  shakeEnabled: boolean;
  onShakeToggle: (enabled: boolean) => void;
  onTriggerSos: () => void;
}

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
});

const EmergencyContacts: React.FC<Props> = ({ shakeEnabled, onShakeToggle, onTriggerSos }) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API_URL}/safety/contacts`, { headers: authHeaders() });
      if (res.ok) setContacts(await res.json());
    } catch { /* offline - keep whatever we have */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const addContact = async () => {
    setError('');
    const p = phone.trim();
    if (p.replace(/[^0-9]/g, '').length < 9) { setError('Enter a valid phone number'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/safety/contacts`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ name: name.trim(), phone: p }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Could not add contact'); return; }
      setContacts((c) => [...c, data]);
      setName(''); setPhone('');
    } catch { setError('Network error - please try again'); }
    finally { setSaving(false); }
  };

  const removeContact = async (id: string) => {
    setContacts((c) => c.filter((x) => x.id !== id)); // optimistic
    try { await fetch(`${API_URL}/safety/contacts/${id}`, { method: 'DELETE', headers: authHeaders() }); }
    catch { /* best-effort; reload will reconcile next open */ }
  };

  // iOS 13+ needs motion permission, requested from this user gesture.
  const toggleShake = async () => {
    if (shakeEnabled) { onShakeToggle(false); return; }
    try {
      const D: any = (window as any).DeviceMotionEvent;
      if (D && typeof D.requestPermission === 'function') {
        const result = await D.requestPermission();
        if (result !== 'granted') {
          setError('Motion access was denied. Enable it in your phone settings to use shake alert.');
          return;
        }
      }
      onShakeToggle(true);
    } catch {
      onShakeToggle(true);
    }
  };

  return (
    <div className="px-4 py-4 space-y-5 pb-24">
      {/* Intro */}
      <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 flex gap-3">
        <ShieldAlert className="w-6 h-6 text-red-400 flex-shrink-0" />
        <div>
          <h3 className="text-white font-bold text-sm">Emergency Contacts</h3>
          <p className="text-white/60 text-xs mt-1">
            In an emergency, we'll text these people your live location and trip details so they can get help to you fast.
          </p>
        </div>
      </div>

      {/* Shake tip + toggle */}
      <div className="bg-safaricab-card border border-white/10 rounded-2xl p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-safaricab-gold" />
            <div>
              <p className="text-white text-sm font-semibold">Shake to alert</p>
              <p className="text-white/50 text-xs">Shake your phone hard to trigger an emergency alert.</p>
            </div>
          </div>
          <button
            onClick={toggleShake}
            className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${shakeEnabled ? 'bg-safaricab-gold' : 'bg-white/15'}`}
            aria-label="Toggle shake alert"
          >
            <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white transition-all ${shakeEnabled ? 'left-[22px]' : 'left-0.5'}`} />
          </button>
        </div>
        {shakeEnabled && (
          <p className="text-safaricab-gold/80 text-[11px] mt-2">
            Shake alert is on. A firm shake starts a 5-second countdown before the alert is sent - so accidental bumps won't fire it.
          </p>
        )}
      </div>

      {/* Contacts list */}
      <div>
        <p className="text-white/50 text-xs mb-2">Your contacts ({contacts.length}/5)</p>
        {loading ? (
          <p className="text-white/40 text-sm">Loading…</p>
        ) : contacts.length === 0 ? (
          <p className="text-white/40 text-sm">No contacts yet. Add someone you trust below.</p>
        ) : (
          <div className="space-y-2">
            {contacts.map((c) => (
              <div key={c.id} className="flex items-center gap-3 bg-safaricab-card rounded-xl px-3 py-3 border border-white/10">
                <div className="w-9 h-9 rounded-full bg-safaricab-gold/20 flex items-center justify-center text-safaricab-gold font-bold text-sm flex-shrink-0">
                  {(c.name || c.phone).charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  {c.name && <p className="text-white text-sm truncate">{c.name}</p>}
                  <p className="text-white/50 text-xs">{c.phone}</p>
                </div>
                <button onClick={() => removeContact(c.id)} className="text-white/40 hover:text-red-400 p-1">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add form */}
      {contacts.length < 5 && (
        <div className="bg-safaricab-card border border-white/10 rounded-2xl p-4 space-y-3">
          <p className="text-white text-sm font-semibold">Add a contact</p>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name (optional)"
            className="w-full bg-safaricab-darker border border-white/10 rounded-xl p-3 text-white text-sm outline-none focus:border-safaricab-gold/50"
          />
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/[^0-9+]/g, ''))}
            placeholder="+2547XXXXXXXX"
            inputMode="tel"
            className="w-full bg-safaricab-darker border border-white/10 rounded-xl p-3 text-white text-sm outline-none focus:border-safaricab-gold/50"
          />
          {error && <p className="text-red-400 text-xs">{error}</p>}
          <button
            onClick={addContact}
            disabled={saving}
            className="w-full bg-safaricab-gold text-black font-bold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" /> {saving ? 'Adding…' : 'Add contact'}
          </button>
        </div>
      )}

      {/* Deliberate manual trigger (replaces the old careless floating button) */}
      <button
        onClick={onTriggerSos}
        className="w-full bg-red-600 text-white font-black py-4 rounded-2xl flex items-center justify-center gap-2"
      >
        <AlertTriangle className="w-5 h-5" /> Send Emergency Alert
      </button>
      <p className="text-white/30 text-[11px] text-center -mt-2">
        A 5-second countdown lets you cancel before it's sent.
      </p>
    </div>
  );
};

export default EmergencyContacts;
